import React, { useEffect, useState, useCallback } from 'react';
import activityLogService from '../services/activityLogService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/activity-logs.css';

const roleLabel = {
  admin: 'Super Admin',
  editor: 'Admin',
  viewer: 'Viewer',
};

const moduleLabels = {
  authentication: 'Sign in / Sign out',
  user_management: 'User accounts',
  permissions: 'Access permissions',
  monitoring: 'Water quality map',
  reports: 'Reports',
  general: 'General',
};

const roleBadgeClass = (role) => {
  const r = String(role || '').toLowerCase();
  if (r === 'admin') return 'al-badge al-badge-role-admin';
  if (r === 'editor') return 'al-badge al-badge-role-editor';
  return 'al-badge al-badge-role-viewer';
};

const moduleBadgeClass = (module) => {
  if (module === 'reports') return 'al-badge al-badge-module-reports';
  if (module === 'monitoring') return 'al-badge al-badge-module-monitoring';
  if (module === 'authentication') return 'al-badge al-badge-module-auth';
  return 'al-badge al-badge-module';
};

/** Plain-language labels for older log rows still stored with technical text */
const friendlyAction = (log) => {
  const action = log?.action || '';
  const path = log?.details?.path || '';

  if (action.startsWith('GET ') || action.startsWith('POST ') || action.startsWith('PUT ') || action.startsWith('DELETE ')) {
    if (path.includes('/wqms/stats')) return 'Checked dashboard summary';
    if (path.includes('/last-record')) return 'Refreshed station reading';
    if (path.includes('/wqms/stations')) return 'Opened water quality map';
    if (path.match(/\/station\/\d+\/trend/)) return 'Viewed station trend chart';
    if (path.match(/\/station\/\d+/) && !path.includes('export')) return 'Opened station details';
    if (path.includes('/export')) return 'Downloaded station data';
    if (path.includes('/reports/')) return 'Opened a report';
    if (path.includes('/timeseries')) return 'Viewed a time chart';
    return 'Used the website';
  }

  const legacy = {
    'User logged in': 'Signed in to the website',
    'User logged out': 'Signed out of the website',
    'Created user account': 'Created a new user account',
    'Updated user account': 'Updated a user account',
    'Deleted user account': 'Deleted a user account',
    'Granted permission': 'Changed access permissions',
    'Updated permission': 'Changed access permissions',
    'Revoked permission': 'Removed access permissions',
  };

  return legacy[action] || action;
};

const moduleOptions = [
  { value: '', label: 'All areas' },
  { value: 'authentication', label: 'Sign in / Sign out' },
  { value: 'user_management', label: 'User accounts' },
  { value: 'permissions', label: 'Access permissions' },
  { value: 'monitoring', label: 'Water quality map' },
  { value: 'reports', label: 'Reports' },
];

const ActivityLogs = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('activity');
  const [logs, setLogs] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ module: '', action: '', email: '' });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await activityLogService.list({
        module: filters.module || undefined,
        action: filters.action || undefined,
        email: filters.email || undefined,
      });
      if (res.success) {
        setLogs(res.data.logs || []);
        setScope(res.data.scope || '');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadActiveUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await activityLogService.activeUsers();
      if (res.success) setActiveUsers(res.data.users || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (tab === 'activity') loadLogs();
    else loadActiveUsers();
  }, [isOpen, tab, loadLogs, loadActiveUsers]);

  if (!isOpen) return null;

  const formatLastSeen = (ts) => {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleString();
  };

  return (
    <div className="activity-logs-overlay" onClick={onClose}>
      <div className="activity-logs-panel" onClick={(e) => e.stopPropagation()}>
        <div className="activity-logs-header">
          <div>
            <h3>Activity &amp; Presence</h3>
            <div className="activity-logs-subtitle">
              {isSuperAdmin
                ? 'Who did what on the website'
                : 'Viewer activity and your own admin actions'}
              {scope === 'viewers_and_self' ? ' (scoped)' : ''}
            </div>
          </div>
          <button type="button" className="activity-logs-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="activity-logs-body">
          <div className="activity-logs-tabs">
            <button
              type="button"
              className={`activity-logs-tab${tab === 'activity' ? ' active' : ''}`}
              onClick={() => setTab('activity')}
            >
              Activity log
            </button>
            <button
              type="button"
              className={`activity-logs-tab${tab === 'presence' ? ' active' : ''}`}
              onClick={() => setTab('presence')}
            >
              Who is active
            </button>
          </div>

          {tab === 'activity' && (
            <>
              <div className="activity-logs-filters">
                <select
                  value={filters.module}
                  onChange={(e) => setFilters((f) => ({ ...f, module: e.target.value }))}
                >
                  {moduleOptions.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Search activity..."
                  value={filters.action}
                  onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Filter by person..."
                  value={filters.email}
                  onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
                />
                <button type="button" className="activity-logs-apply" onClick={loadLogs}>Apply filters</button>
              </div>

              {loading ? (
                <div className="activity-logs-loading">Loading logs...</div>
              ) : (
                <div className="activity-logs-table-wrap">
                  <table className="activity-logs-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Who did it</th>
                        <th>Their role</th>
                        <th>What happened</th>
                        <th>Area</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr className="activity-logs-empty">
                          <td colSpan={5}>No activity recorded yet.</td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          <tr key={log.id}>
                            <td className="activity-logs-time">{new Date(log.createdAt).toLocaleString()}</td>
                            <td className="activity-logs-email">{log.actorEmail || '—'}</td>
                            <td>
                              <span className={roleBadgeClass(log.actorRole)}>
                                {roleLabel[log.actorRole] || log.actorRole || '—'}
                              </span>
                            </td>
                            <td className="activity-logs-action">{friendlyAction(log)}</td>
                            <td>
                              <span className={moduleBadgeClass(log.module)}>
                                {moduleLabels[log.module] || log.module || '—'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === 'presence' && (
            <>
              <p className="activity-logs-hint">
                Online = activity in the last 15 minutes.
              </p>
              {loading ? (
                <div className="activity-logs-loading">Loading users...</div>
              ) : (
                <div className="activity-logs-table-wrap">
                  <table className="activity-logs-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeUsers.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <strong>{u.firstName} {u.lastName}</strong>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{u.email}</div>
                          </td>
                          <td>
                            <span className={roleBadgeClass(u.role)}>
                              {roleLabel[u.role] || u.role}
                            </span>
                          </td>
                          <td>
                            <span className={u.isOnline ? 'al-status-online' : 'al-status-offline'}>
                              {u.isOnline ? 'Online' : 'Offline'}
                            </span>
                          </td>
                          <td className="activity-logs-time">{formatLastSeen(u.lastSeenAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
